import React, { useState } from 'react';
import type { TableColumnsType } from 'antd';
import { Table, Button, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { RealtimeSettlementOrderDailySumType, RealtimeSettlementOrderDailySumFilterType } from '../graphql/types/RealtimeSettlementOrderDailySumType';
import { EXPORT_REALTIME_SETTLEMENT_ORDER_DAILY_DETAIL, ExportRealtimeSettlementOrderDailyDetailResponse } from '../graphql/mutations/exportRealtimeSettlementOrderDailyDetail';
import { HasResource } from 'components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';
import { formatPlainAmountFromCents } from 'utils/settlementAmountFormatter';
import { formatYyyymmdd } from 'utils/invoiceAmount';

interface RealtimeSettlementOrderDailySumRowProps {
    rowData: RealtimeSettlementOrderDailySumType[];
    loading?: boolean;
    filters?: RealtimeSettlementOrderDailySumFilterType;
}

export const RealtimeSettlementOrderDailySumColumns: React.FC<RealtimeSettlementOrderDailySumRowProps> = ({
    rowData,
    loading,
    filters
}) => {
    const [exportingId, setExportingId] = useState<string | null>(null);

    const [exportMutation] = useMutation<ExportRealtimeSettlementOrderDailyDetailResponse>(
        EXPORT_REALTIME_SETTLEMENT_ORDER_DAILY_DETAIL
    );

    const handleExport = async (record: RealtimeSettlementOrderDailySumType) => {
        if (!filters?.dailyDateStart || !filters?.dailyDateEnd) {
            message.warning('请先选择日期范围');
            return;
        }

        setExportingId(record.id);
        try {
            const result = await exportMutation({
                variables: {
                    filters: {
                        clientIds: [Number(record.clientId)],
                        dailyDateStart: filters.dailyDateStart,
                        dailyDateEnd: filters.dailyDateEnd
                    }
                }
            });

            if (result.data?.exportRealtimeSettlementOrderDailyDetail) {
                message.success(`导出任务已创建: ${result.data.exportRealtimeSettlementOrderDailyDetail}，请在下载中心查看`);
            }
        } catch (error: any) {
            message.error(`导出失败: ${error.message || '未知错误'}`);
        } finally {
            setExportingId(null);
        }
    };

    const columns: TableColumnsType<RealtimeSettlementOrderDailySumType> = [
        {
            title: '客户名称',
            dataIndex: 'clientName',
            key: 'clientName',
            align: 'center',
        },
        {
            title: '客户ID',
            dataIndex: 'clientId',
            key: 'clientId',
            align: 'center',
        },
        {
            title: '订单数',
            dataIndex: 'orderCount',
            key: 'orderCount',
            align: 'center',
        },
        {
            title: '时间范围',
            dataIndex: 'dailyDate',
            key: 'dailyDate',
            align: 'center',
            render: (dailyDate: string) => formatYyyymmdd(Number(dailyDate)),
        },
        {
            title: '币种',
            dataIndex: 'currency',
            key: 'currency',
            align: 'center',
            render: (currency) => {
                const currencyMap: Record<string, string> = {
                    'USD': '美元',
                    'CNY': '人民币'
                };
                return currencyMap[currency] || currency;
            },
        },
        {
            title: '总金额',
            key: 'sumAmount',
            align: 'center',
            render: (_, record) => formatPlainAmountFromCents(record.sumAmount),
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            width: 120,
            render: (_, record) => (
                <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REALTIME_EXPORT_DETAIL]}>
                    <Button
                        type="link"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => handleExport(record)}
                        loading={exportingId !== null && exportingId === record.id}
                        disabled={exportingId !== null && exportingId !== record.id}
                    >
                        导出明细
                    </Button>
                </HasResource>
            ),
        }
    ];

    return (
        <Table
            rowKey={(record) => record.id}
            columns={columns}
            dataSource={rowData}
            scroll={{ x: 1950 }}
            size="middle"
            className="mb-4"
            pagination={false}
            loading={loading}
        />
    );
};
